/**
 * coloringSchemas.js
 *
 * Phase 1 vocabulary + validator for the reusable dashboard coloring-schema engine (see
 * .claude/features/COLORING_SCHEMA_RESEARCH.md §4). A schema is a direction-free lookup
 * table — either a category schema (exact-match `entries`) or a number schema (ordered
 * half-open `[from, to)` ranges) — mapping a widget's measure to a semantic `tone` or a raw
 * `color` pair, or to one of the failure render states.
 *
 * This file holds the DEFINITIONS half of the module:
 *   • the enums (no magic strings),
 *   • the six built-in read-only schemas + their registry (§4.5),
 *   • one canonical `validateSchema` used by BOTH the wizard (save) and the render path (§4.8).
 *
 * The ENGINE half — the memoized `resolveSchema` that compiles a schema into a hot-path
 * lookup, applying unit conversion and emitting the §4.4 render states — lives in
 * `coloringSchemaEngine.js`, which imports from this file (one direction, no cycle).
 *
 * PURE module — no Vuex, no components. Re-scoped 2026-07-17: the three number built-ins are
 * STATIC over the FIXED default inspection bands (imported from `inspectionBandDefaults.js`),
 * identical for every facility; per-facility colors are Phase 2. The engine retains a separate
 * facility-injected `bandBacked` path for that future work, with no Phase-1 user. Deviations
 * from the doc's stated sources are noted inline.
 */

import { CONDITION_RATING } from '@/features/Tags/components/monitoring/cmlColoringConstants';
import { error as debugError } from '@/utils/debugLogger';
import {
  CORROSION_RATE_BANDS,
  REMAINING_LIFE_BANDS,
  THICKNESS_BANDS,
  INSPECTION_BAND_UNITS,
} from './inspectionBandDefaults';

export { UNIT, convertUnit, canConvert } from './unitConversion';

// ─── Enums (no magic strings) ──────────────────────────────────────────────────

// Widget coloring source — the mutually exclusive dropdown value (§4.1).
export const COLORING_SOURCE = Object.freeze({
  STATIC: 'static',
  DIRECTION: 'direction',
  SCHEMA: 'schema',
});

// schemaSelection discriminated-union tag (§4.3).
export const SCHEMA_KIND = Object.freeze({
  BUILTIN: 'builtin',
  INLINE: 'inline',
  REFERENCE: 'reference',
});

// The five lookup outcomes (§4.4). A real measurement is NEVER painted with another
// schema's colors; an unavailable schema surfaces a visible state, not a wrong color.
export const RENDER_STATE = Object.freeze({
  MATCHED: 'matched',
  NO_DATA: 'noData',
  UNMAPPED_VALUE: 'unmappedValue',
  SCHEMA_UNAVAILABLE: 'schemaUnavailable',
  LOAD_ERROR: 'loadError',
});

// Schema form (§4.2): exact-match categories vs half-open number ranges.
export const APPLIES_TO = Object.freeze({
  NUMBER: 'number',
  CATEGORY: 'category',
});

// Semantic tone roles — resolved to WCAG-paired hexes by the shipped tone table at the
// caller (a `tone` MAY color text; a raw `color` may not — §4.9).
export const TONE = Object.freeze({
  GOOD: 'good',
  CAUTION: 'caution',
  CRITICAL: 'critical',
});

// EDITOR-PREVIEW swatch for each tone — one representative hex used by the manager dialog and the
// band/entry editors to paint their read-only preview strips. This is NOT the authoritative render
// tone table (the render path resolves tones to WCAG-paired hexes at the caller, §4.9); it exists
// only so an editor swatch shows roughly the color a tone conveys. A tone-less item (raw color)
// paints its own `color.light`; anything unrecognised falls back to NEUTRAL_SWATCH.
export const TONE_SWATCH = Object.freeze({
  [TONE.GOOD]: '#03624c',
  [TONE.CAUTION]: '#a07a00',
  [TONE.CRITICAL]: '#b71c1c',
});
export const NEUTRAL_SWATCH = '#9E9E9E';

/**
 * Resolve a preview swatch hex for a schema entry or range: its tone's representative swatch, else
 * its raw `color.light`, else the neutral grey. Null-safe so it can paint an incomplete editor row.
 * @param {{ tone?: string, color?: { light?: string } }} item
 * @returns {string} a hex color
 */
export function paintSwatch(item) {
  if (item && item.tone) return TONE_SWATCH[item.tone] || NEUTRAL_SWATCH;
  if (item && item.color && item.color.light) return item.color.light;
  return NEUTRAL_SWATCH;
}

// Where a schema's definition comes from (§4.2 `source`).
export const SCHEMA_SOURCE = Object.freeze({
  BUILT_IN: 'built-in',
  DASHBOARD: 'dashboard', // tab/dashboard-scoped registry schemas (the manager dialog)
  FACILITY: 'facility',
  WIDGET: 'widget',
});

// The six Phase 1 built-in schema ids (§4.5).
export const SCHEMA_ID = Object.freeze({
  CML_CONDITION: 'cml-condition',
  SEVERITY: 'severity',
  RAG: 'rag',
  CORROSION_RATE: 'corrosion-rate',
  REMAINING_LIFE: 'remaining-life',
  THICKNESS: 'thickness',
});

// Theme selector for resolving a `{light, dark}` color pair to one hex (memo key, §4.8).
export const THEME = Object.freeze({
  LIGHT: 'light',
  DARK: 'dark',
});

// ─── Raw hues with no verified tone pair (§4.9) ─────────────────────────────────
// These render as a fill/chip ONLY, never as text. There is no shared importable hex map
// for them, so they are declared here with their canonical source noted. Where a verified
// tone exists (green→good, amber→caution, red→critical) the entry uses `tone` instead, so
// only the genuinely tone-less hues are redeclared below.

// MUST MIRROR `_getCmlConditionColor` (forgeService.js:4197-4198): a class method on a heavy
// service, not importable into a pure module; cmlColoringConstants.js exposes only the grey.
const CML_REINSPECT_COLOR = Object.freeze({ light: '#FF9800', dark: '#FF9800' }); // orange
const CML_FFS_REQUIRED_COLOR = Object.freeze({ light: '#9C27B0', dark: '#9C27B0' }); // purple

// MUST MIRROR `SEVERITY_COLORS['High']` (echartsOptions.js:34): that map is module-local
// (not exported), so the one tone-less severity hue is redeclared here.
const SEVERITY_HIGH_COLOR = Object.freeze({ light: '#d97706', dark: '#d97706' }); // orange

// ─── The six built-in schemas (§4.5) ────────────────────────────────────────────

// Recursively freeze a definition so entries, ranges and {light,dark} pairs are all
// immutable (a shallow Object.freeze would leave nested members writable).
function deepFreeze(value) {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.values(value).forEach(deepFreeze);
    Object.freeze(value);
  }
  return value;
}

// 1. CML Condition — five workflow levels + no-data. Value keys are the backend enum names
//    (CONDITION_RATING). Acceptable/Monitor/RepairRequired route through tones; ReInspect
//    and FfsRequired have no tone, so they carry raw color pairs (§4.5, §4.9).
const CML_CONDITION_SCHEMA = deepFreeze({
  id: SCHEMA_ID.CML_CONDITION,
  name: 'CML Condition (5 levels)',
  appliesTo: APPLIES_TO.CATEGORY,
  source: SCHEMA_SOURCE.BUILT_IN,
  entries: [
    { value: CONDITION_RATING.ACCEPTABLE, tone: TONE.GOOD, label: 'Acceptable' },
    { value: CONDITION_RATING.MONITOR, tone: TONE.CAUTION, label: 'Monitor' },
    { value: CONDITION_RATING.RE_INSPECT, color: CML_REINSPECT_COLOR, label: 'Re-inspect' },
    { value: CONDITION_RATING.FFS_REQUIRED, color: CML_FFS_REQUIRED_COLOR, label: 'FFS required' },
    { value: CONDITION_RATING.REPAIR_REQUIRED, tone: TONE.CRITICAL, label: 'Repair required' },
  ],
  fallback: { label: 'Other' },
  noData: { label: 'No data' },
});

// 2. Severity — four levels. Low/Medium/High-Repair route through tones; the plain High
//    level has no verified tone pair, so it carries a raw color (§4.9).
const SEVERITY_SCHEMA = deepFreeze({
  id: SCHEMA_ID.SEVERITY,
  name: 'Severity (4 levels)',
  appliesTo: APPLIES_TO.CATEGORY,
  source: SCHEMA_SOURCE.BUILT_IN,
  entries: [
    { value: 'Low', tone: TONE.GOOD, label: 'Low' },
    { value: 'Medium', tone: TONE.CAUTION, label: 'Medium' },
    { value: 'High', color: SEVERITY_HIGH_COLOR, label: 'High' },
    { value: 'High (Repair-Required)', tone: TONE.CRITICAL, label: 'High (Repair-Required)' },
  ],
  noData: { label: 'No data' },
});

// 3. RAG — three levels, fully tone-routed (High→critical, Medium→caution, Low→good), so
//    no raw hex is redeclared; the tones carry the red/amber/green semantics (§4.5, §4.9).
const RAG_SCHEMA = deepFreeze({
  id: SCHEMA_ID.RAG,
  name: 'High / Medium / Low',
  appliesTo: APPLIES_TO.CATEGORY,
  source: SCHEMA_SOURCE.BUILT_IN,
  entries: [
    { value: 'High', tone: TONE.CRITICAL, label: 'High' },
    { value: 'Medium', tone: TONE.CAUTION, label: 'Medium' },
    { value: 'Low', tone: TONE.GOOD, label: 'Low' },
  ],
  noData: { label: 'No data' },
});

// Convert a default band array (bucketOf/store shape) into canonical half-open [from, to)
// ranges. Reads the deep-frozen source read-only and returns NEW objects (no mutation, no
// clone needed); drops the inert N/A bucket (null values resolve to noData grey). The defaults
// follow the fixed convention judgMkr1 '<' (upper exclusive) + judgMkr2 '>='/'>' (lower
// inclusive), so [from, to) = [judgCrr2, judgCrr1) with the ±∞ sentinels mapped to null
// (unbounded). A band hue becomes a raw {light, dark} color pair — a fill/chip, never a tone (§4.9).
function bandsToRanges(bands) {
  return bands
    .filter((band) => band.judgMkr1 != null && band.judgMkr2 != null) // drop N/A (both markers null)
    .map((band) => ({
      from: band.judgCrr2 === '-∞' ? null : band.judgCrr2,
      to: band.judgCrr1 === '∞' ? null : band.judgCrr1,
      color: { light: band.judgCor, dark: band.judgCor },
      label: band.title,
    }));
}

// 4-6. Corrosion Rate / Remaining Life / Thickness — number schemas over the FIXED default
//      inspection bands (re-scoped 2026-07-17: static, identical for every facility; per-facility
//      colors are Phase 2). The default arrays (from inspectionBandDefaults.js) are converted ONCE
//      at module load into canonical `ranges`, so the engine resolves them with NO injected context
//      (no `bandBacked` flag) and the wizard renders them from `schema.ranges` like any authored
//      schema. Band hues are arbitrary preset colors, so each range carries a raw `color` pair (§4.9).
function defaultBandsSchema(id, name, unit, bands) {
  return deepFreeze({
    id,
    name,
    appliesTo: APPLIES_TO.NUMBER,
    source: SCHEMA_SOURCE.BUILT_IN,
    unit,
    ranges: bandsToRanges(bands),
    noData: { label: 'No data' },
  });
}
const CORROSION_RATE_SCHEMA = defaultBandsSchema(
  SCHEMA_ID.CORROSION_RATE, 'Corrosion Rate Bands', INSPECTION_BAND_UNITS.corrosionRate, CORROSION_RATE_BANDS,
);
const REMAINING_LIFE_SCHEMA = defaultBandsSchema(
  SCHEMA_ID.REMAINING_LIFE, 'Remaining Life Bands', INSPECTION_BAND_UNITS.remainingLife, REMAINING_LIFE_BANDS,
);
const THICKNESS_SCHEMA = defaultBandsSchema(
  SCHEMA_ID.THICKNESS, 'Thickness Bands', INSPECTION_BAND_UNITS.thickness, THICKNESS_BANDS,
);

export const BUILT_IN_SCHEMAS = Object.freeze({
  [SCHEMA_ID.CML_CONDITION]: CML_CONDITION_SCHEMA,
  [SCHEMA_ID.SEVERITY]: SEVERITY_SCHEMA,
  [SCHEMA_ID.RAG]: RAG_SCHEMA,
  [SCHEMA_ID.CORROSION_RATE]: CORROSION_RATE_SCHEMA,
  [SCHEMA_ID.REMAINING_LIFE]: REMAINING_LIFE_SCHEMA,
  [SCHEMA_ID.THICKNESS]: THICKNESS_SCHEMA,
});

/** Built-in schema definition by id, or null. */
export function getBuiltInSchema(id) {
  return BUILT_IN_SCHEMAS[id] || null;
}

// ─── Validation (§4.8) ──────────────────────────────────────────────────────────

const VALID_TONES = new Set(Object.values(TONE));

function isColorPair(color) {
  return !!color
    && typeof color === 'object'
    && typeof color.light === 'string' && color.light.trim() !== ''
    && typeof color.dark === 'string' && color.dark.trim() !== '';
}

// An entry/range must carry EXACTLY one of tone|color; tone must be known; color must be a
// valid {light,dark} pair (§4.8, §4.9).
function validatePaint(item, label, errors) {
  const hasTone = item.tone != null;
  const hasColor = item.color != null;
  if (hasTone === hasColor) {
    errors.push(`${label}: exactly one of tone|color is required`);
    return;
  }
  if (hasTone && !VALID_TONES.has(item.tone)) errors.push(`${label}: unknown tone "${item.tone}"`);
  if (hasColor && !isColorPair(item.color)) errors.push(`${label}: color must be a {light, dark} hex pair`);
}

// Coerce a range boundary for ordering: null lower → -Infinity, null upper → +Infinity.
function lowerBound(range) { return range.from == null ? -Infinity : range.from; }
function upperBound(range) { return range.to == null ? Infinity : range.to; }

function validateNumberRanges(schema, errors) {
  const ranges = schema.ranges;
  if (!Array.isArray(ranges) || ranges.length === 0) {
    errors.push('ranges: a non-empty ranges array is required for a number schema');
    return;
  }
  const covered = schema.noData != null || schema.fallback != null;
  let prevUpper = null;
  ranges.forEach((range, i) => {
    const lo = lowerBound(range);
    const hi = upperBound(range);
    if (!(lo < hi)) errors.push(`ranges[${i}]: from must be strictly below to`);
    if (prevUpper != null) {
      if (lo < prevUpper) errors.push(`ranges[${i}]: overlaps the previous range`);
      else if (lo > prevUpper && !covered) errors.push(`ranges[${i}]: gap before this range needs noData or fallback`);
    }
    validatePaint(range, `ranges[${i}]`, errors);
    prevUpper = hi;
  });
}

function validateCategoryEntries(schema, errors) {
  const entries = schema.entries;
  if (!Array.isArray(entries) || entries.length === 0) {
    errors.push('entries: a non-empty entries array is required for a category schema');
    return;
  }
  const seen = new Set();
  entries.forEach((entry, i) => {
    if (entry.value == null || String(entry.value).trim() === '') {
      errors.push(`entries[${i}]: a non-blank value is required`);
    } else {
      const key = String(entry.value).trim().toLowerCase();
      if (seen.has(key)) errors.push(`entries[${i}]: duplicate value "${entry.value}"`);
      seen.add(key);
    }
    validatePaint(entry, `entries[${i}]`, errors);
  });
}

/**
 * Canonical schema validator (§4.8). Runs at save time (the wizard blocks an invalid save)
 * and at resolve time (a malformed stored schema yields `schemaUnavailable`, never a crash
 * or mis-color). Band-backed number schemas skip range checks — their ranges are injected
 * facility data validated by the store, not authored here.
 * @param {object} schema
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validateSchema(schema) {
  const errors = [];
  if (!schema || typeof schema !== 'object') {
    errors.push('schema must be an object');
    return { valid: false, errors };
  }
  if (schema.appliesTo === APPLIES_TO.CATEGORY) {
    validateCategoryEntries(schema, errors);
    if (schema.fallback != null && schema.fallback.tone == null && schema.fallback.color == null
        && (schema.fallback.label == null || String(schema.fallback.label).trim() === '')) {
      errors.push('fallback: needs a label, tone or color');
    }
  } else if (schema.appliesTo === APPLIES_TO.NUMBER) {
    if (schema.unit == null || String(schema.unit).trim() === '') errors.push('unit: required for a number schema');
    // Built-in default-band schemas and inline custom schemas both carry canonical `ranges`; only
    // the Phase-2 facility-injected (`bandBacked`) path defers range validation to load time.
    if (!schema.bandBacked) validateNumberRanges(schema, errors);
  } else {
    errors.push(`appliesTo must be "${APPLIES_TO.NUMBER}" or "${APPLIES_TO.CATEGORY}"`);
  }
  if (errors.length > 0) debugError('[coloringSchemas] invalid schema', schema && schema.id, errors);
  return { valid: errors.length === 0, errors };
}
